import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import {
  CreateBankAccountSchema,
  UpdateBankAccountSchema,
} from "./bank-account.schema";

export const BankAccountIdInputSchema = z.object({
  id: z.string().trim().min(1),
  businessId: z.string().trim().min(1),
});

export const ListBankAccountsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  search: z.string().optional(),
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  ids: z.array(z.string().trim().min(1)).max(500).optional(),
});

export const CreateBankAccountInputSchema = z.object({
  businessId: z.string().trim().min(1),
  item: CreateBankAccountSchema,
});

export const UpdateBankAccountInputSchema = z.object({
  ...BankAccountIdInputSchema.shape,
  updates: UpdateBankAccountSchema,
});

export const listBankAccounts = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => ListBankAccountsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankAccounts.list", async () => {
      const { listBankAccountsForUser } = await import("./bank-account.server");
      return listBankAccountsForUser(context.user.id, data.businessId, {
        search: data.search,
        offset: data.offset,
        limit: data.limit,
        ids: data.ids,
      });
    });
  });

export const getBankAccount = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BankAccountIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankAccounts.get", async () => {
      const { getBankAccountForUser } = await import("./bank-account.server");
      const result = await getBankAccountForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const createBankAccount = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => CreateBankAccountInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankAccounts.create", async () => {
      const { createBankAccountForUser } =
        await import("./bank-account.server");
      return createBankAccountForUser(
        context.user.id,
        data.businessId,
        data.item,
      );
    });
  });

export const updateBankAccount = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => UpdateBankAccountInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankAccounts.update", async () => {
      const { updateBankAccountForUser } =
        await import("./bank-account.server");
      const result = await updateBankAccountForUser(
        context.user.id,
        data.id,
        data.updates,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const deleteBankAccount = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BankAccountIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankAccounts.delete", async () => {
      const { deleteBankAccountForUser } =
        await import("./bank-account.server");
      const result = await deleteBankAccountForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });
